import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Loader2, Trash2, ArrowUp, ArrowDown, MapPin, Save } from "lucide-react";
import { toast } from "sonner";
import { GlassCard } from "../../components/common/ui";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import api, { formatApiError } from "../../lib/api";

const inp = "mt-1 bg-black/30 border-white/15 text-white";
const selCls = "bg-black/40 border border-white/15 text-white text-xs rounded-md px-2 py-1.5 outline-none focus:border-gold/40";

const EMPTY_GROUP = { name: "", slug: "", intro: "", seo_title: "", meta_description: "" };
const EMPTY_LOC = { name: "", alt_name: "", note: "", map_url: "" };

export default function AdminAreas() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [groupDlg, setGroupDlg] = useState({ open: false, editing: null, form: EMPTY_GROUP });
  const [locDlg, setLocDlg] = useState({ open: false, editing: null, groupId: null, form: EMPTY_LOC });
  const [newLoc, setNewLoc] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/areas").then(({ data }) => setGroups(data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const err = (e) => toast.error(formatApiError(e.response?.data?.detail) || "Something went wrong");

  // ---- groups ----
  const saveGroup = async () => {
    const { editing, form } = groupDlg;
    if (!form.name.trim()) return toast.error("Group name is required");
    setSaving(true);
    try {
      if (editing) await api.patch(`/admin/areas/groups/${editing.group_id}`, form);
      else await api.post("/admin/areas/groups", form);
      toast.success("Group saved");
      setGroupDlg({ open: false, editing: null, form: EMPTY_GROUP });
      load();
    } catch (e) { err(e); } finally { setSaving(false); }
  };

  const deleteGroup = async (g) => {
    if (!window.confirm(`Delete group "${g.name}" and all its locations? This cannot be undone.`)) return;
    try { await api.delete(`/admin/areas/groups/${g.group_id}`); toast.success("Group deleted"); load(); } catch (e) { err(e); }
  };

  const toggleGroup = async (g, active) => {
    try { await api.patch(`/admin/areas/groups/${g.group_id}`, { active }); load(); } catch (e) { err(e); }
  };

  const moveGroup = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= groups.length) return;
    const order = groups.map((g) => g.group_id);
    [order[idx], order[j]] = [order[j], order[idx]];
    try { await api.post("/admin/areas/groups/reorder", { order }); load(); } catch (e) { err(e); }
  };

  // ---- locations ----
  const addLocation = async (groupId) => {
    const name = (newLoc[groupId] || "").trim();
    if (!name) return;
    try {
      await api.post("/admin/areas/locations", { group_id: groupId, name });
      setNewLoc((n) => ({ ...n, [groupId]: "" }));
      load();
    } catch (e) { err(e); }
  };

  const saveLocation = async () => {
    const { editing, form } = locDlg;
    if (!form.name.trim()) return toast.error("Location name is required");
    setSaving(true);
    try {
      await api.patch(`/admin/areas/locations/${editing.location_id}`, form);
      toast.success("Location saved");
      setLocDlg({ open: false, editing: null, groupId: null, form: EMPTY_LOC });
      load();
    } catch (e) { err(e); } finally { setSaving(false); }
  };

  const deleteLocation = async (l) => {
    if (!window.confirm(`Delete "${l.name}"?`)) return;
    try { await api.delete(`/admin/areas/locations/${l.location_id}`); load(); } catch (e) { err(e); }
  };

  const toggleLocation = async (l, active) => {
    try { await api.patch(`/admin/areas/locations/${l.location_id}`, { active }); load(); } catch (e) { err(e); }
  };

  const moveLocationToGroup = async (l, groupId) => {
    if (groupId === l.group_id) return;
    try { await api.patch(`/admin/areas/locations/${l.location_id}`, { group_id: groupId }); toast.success("Location moved"); load(); } catch (e) { err(e); }
  };

  const moveLocationOrder = async (group, idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= group.locations.length) return;
    const order = group.locations.map((l) => l.location_id);
    [order[idx], order[j]] = [order[j], order[idx]];
    try { await api.post("/admin/areas/locations/reorder", { group_id: group.group_id, order }); load(); } catch (e) { err(e); }
  };

  const openNewGroup = () => setGroupDlg({ open: true, editing: null, form: EMPTY_GROUP });
  const openEditGroup = (g) => setGroupDlg({ open: true, editing: g, form: { name: g.name || "", slug: g.slug || "", intro: g.intro || "", seo_title: g.seo_title || "", meta_description: g.meta_description || "" } });
  const openEditLoc = (l) => setLocDlg({ open: true, editing: l, groupId: l.group_id, form: { name: l.name || "", alt_name: l.alt_name || "", note: l.note || "", map_url: l.map_url || "" } });

  const gForm = (k, v) => setGroupDlg((d) => ({ ...d, form: { ...d.form, [k]: v } }));
  const lForm = (k, v) => setLocDlg((d) => ({ ...d, form: { ...d.form, [k]: v } }));

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin text-gold" /> Loading…</div>;

  return (
    <div className="space-y-6" data-testid="admin-areas">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-playfair text-2xl sm:text-3xl text-white">Areas We Serve</h1>
          <p className="mt-2 text-muted-foreground">Manage the area groups and locations shown on the public <a href="/areas-we-serve" target="_blank" rel="noreferrer" className="text-gold hover:underline">Areas We Serve</a> page. Changes appear immediately — no redeployment needed.</p>
        </div>
        <Button data-testid="add-group-btn" onClick={openNewGroup} className="cf-gold-btn font-semibold gap-2"><Plus className="h-4 w-4" /> Add Group</Button>
      </div>

      {groups.length === 0 ? (
        <GlassCard className="p-10 text-center text-muted-foreground" hover={false}>No area groups yet. Add one to get started.</GlassCard>
      ) : (
        <div className="space-y-5">
          {groups.map((g, gi) => (
            <GlassCard key={g.group_id} className="p-5" hover={false} data-testid={`admin-group-${g.slug}`}>
              <div className="flex flex-wrap items-center gap-3">
                <Switch data-testid={`group-active-${g.slug}`} checked={g.active !== false} onCheckedChange={(v) => toggleGroup(g, v)} />
                <div className="flex-1 min-w-[160px]">
                  <p className="text-white font-medium">{g.name}</p>
                  <p className="text-[11px] text-white/40">/{g.slug} · {g.locations.length} location{g.locations.length === 1 ? "" : "s"} · {g.active !== false ? <span className="text-emerald-400">Active</span> : <span className="text-amber">Hidden</span>}</p>
                </div>
                <button type="button" onClick={() => moveGroup(gi, -1)} className="text-white/50 hover:text-white p-1.5" data-testid={`group-up-${g.slug}`}><ArrowUp className="h-4 w-4" /></button>
                <button type="button" onClick={() => moveGroup(gi, 1)} className="text-white/50 hover:text-white p-1.5" data-testid={`group-down-${g.slug}`}><ArrowDown className="h-4 w-4" /></button>
                <Button variant="outline" size="sm" className="border-white/20 text-white gap-1.5" data-testid={`edit-group-${g.slug}`} onClick={() => openEditGroup(g)}><Pencil className="h-3.5 w-3.5" /> Edit</Button>
                <button type="button" onClick={() => deleteGroup(g)} className="text-firered/80 hover:text-firered p-1.5" data-testid={`delete-group-${g.slug}`}><Trash2 className="h-4 w-4" /></button>
              </div>
              {g.intro && <p className="mt-2 text-sm text-white/50">{g.intro}</p>}

              <div className="mt-4 space-y-1.5" data-testid={`group-locations-${g.slug}`}>
                {g.locations.map((l, li) => (
                  <div key={l.location_id} className="flex flex-wrap items-center gap-2 rounded-lg border border-white/10 bg-black/20 px-3 py-2" data-testid={`admin-location-${l.location_id}`}>
                    <Switch data-testid={`location-active-${l.location_id}`} checked={l.active !== false} onCheckedChange={(v) => toggleLocation(l, v)} />
                    <span className={`flex-1 min-w-[120px] text-sm ${l.active !== false ? "text-white/85" : "text-white/40 line-through"}`}>
                      {l.name}{l.alt_name ? <span className="text-white/40"> ({l.alt_name})</span> : null}
                      {l.map_url && <MapPin className="inline h-3 w-3 text-gold/70 ml-1.5" />}
                    </span>
                    <select value={l.group_id} onChange={(e) => moveLocationToGroup(l, e.target.value)} className={selCls} data-testid={`location-move-${l.location_id}`} title="Move to group">
                      {groups.map((gg) => <option key={gg.group_id} value={gg.group_id}>{gg.name}</option>)}
                    </select>
                    <button type="button" onClick={() => moveLocationOrder(g, li, -1)} className="text-white/50 hover:text-white p-1"><ArrowUp className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => moveLocationOrder(g, li, 1)} className="text-white/50 hover:text-white p-1"><ArrowDown className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => openEditLoc(l)} className="text-white/60 hover:text-gold p-1" data-testid={`edit-location-${l.location_id}`}><Pencil className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => deleteLocation(l)} className="text-firered/70 hover:text-firered p-1" data-testid={`delete-location-${l.location_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>

              <div className="mt-3 flex items-center gap-2">
                <Input value={newLoc[g.group_id] || ""} onChange={(e) => setNewLoc((n) => ({ ...n, [g.group_id]: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === "Enter") addLocation(g.group_id); }}
                  placeholder="Add a town, village or hamlet…" className="bg-black/30 border-white/15 text-white text-sm h-9 max-w-xs" data-testid={`new-location-input-${g.slug}`} />
                <Button size="sm" variant="outline" className="border-white/20 text-white gap-1.5" data-testid={`add-location-${g.slug}`} onClick={() => addLocation(g.group_id)}><Plus className="h-3.5 w-3.5" /> Add</Button>
              </div>
            </GlassCard>
          ))}
        </div>
      )}

      {/* Group dialog */}
      <Dialog open={groupDlg.open} onOpenChange={(o) => setGroupDlg((d) => ({ ...d, open: o }))}>
        <DialogContent className="cf-glass border-white/10 text-white max-w-lg" data-testid="group-dialog">
          <DialogHeader>
            <DialogTitle>{groupDlg.editing ? "Edit Group" : "New Group"}</DialogTitle>
            <DialogDescription className="text-white/50">Area groups organise the towns and villages shown on the public page.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-white/80">Group name</Label><Input data-testid="group-name-input" value={groupDlg.form.name} onChange={(e) => gForm("name", e.target.value)} className={inp} /></div>
            <div><Label className="text-white/80">Slug <span className="text-white/40">(optional — auto-generated)</span></Label><Input value={groupDlg.form.slug} onChange={(e) => gForm("slug", e.target.value)} className={inp} /></div>
            <div><Label className="text-white/80">Short introduction</Label><Textarea value={groupDlg.form.intro} onChange={(e) => gForm("intro", e.target.value)} className={inp} /></div>
            <div><Label className="text-white/80">SEO title <span className="text-white/40">(optional)</span></Label><Input value={groupDlg.form.seo_title} onChange={(e) => gForm("seo_title", e.target.value)} className={inp} /></div>
            <div><Label className="text-white/80">Meta description <span className="text-white/40">(optional)</span></Label><Textarea value={groupDlg.form.meta_description} onChange={(e) => gForm("meta_description", e.target.value)} className={inp} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-white/20 text-white" onClick={() => setGroupDlg({ open: false, editing: null, form: EMPTY_GROUP })}>Cancel</Button>
            <Button data-testid="save-group-btn" onClick={saveGroup} disabled={saving} className="cf-gold-btn font-semibold gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Location dialog */}
      <Dialog open={locDlg.open} onOpenChange={(o) => setLocDlg((d) => ({ ...d, open: o }))}>
        <DialogContent className="cf-glass border-white/10 text-white max-w-lg" data-testid="location-dialog">
          <DialogHeader>
            <DialogTitle>Edit Location</DialogTitle>
            <DialogDescription className="text-white/50">Optional note, alternate name and map link.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-white/80">Location name</Label><Input data-testid="location-name-input" value={locDlg.form.name} onChange={(e) => lForm("name", e.target.value)} className={inp} /></div>
            <div><Label className="text-white/80">Alternate name <span className="text-white/40">(optional)</span></Label><Input value={locDlg.form.alt_name} onChange={(e) => lForm("alt_name", e.target.value)} className={inp} /></div>
            <div><Label className="text-white/80">Note <span className="text-white/40">(optional)</span></Label><Textarea value={locDlg.form.note} onChange={(e) => lForm("note", e.target.value)} className={inp} /></div>
            <div><Label className="text-white/80">Map URL <span className="text-white/40">(optional)</span></Label><Input value={locDlg.form.map_url} onChange={(e) => lForm("map_url", e.target.value)} className={inp} placeholder="https://maps.google.com/…" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-white/20 text-white" onClick={() => setLocDlg({ open: false, editing: null, groupId: null, form: EMPTY_LOC })}>Cancel</Button>
            <Button data-testid="save-location-btn" onClick={saveLocation} disabled={saving} className="cf-gold-btn font-semibold gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
